// API bindings: notifications (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { NotificationCountUnreadResponse, NotificationList, NotificationMarkReadBody, NotificationAdminSettings } from "./definitions";

export class ApiNotifications {
    /**
     * Method: GET
     * Path: /notifications/unread
     * Counts unread notifications
     * Requires permission: notification.read
     * @returns The request parameters
     */
    public static CountUnread(): RequestParams<NotificationCountUnreadResponse, CountUnreadErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/notifications/unread`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /notifications/list
     * List notifications
     * Requires permission: notification.read
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListNotifications(queryParams: ListNotificationsQueryParameters): RequestParams<NotificationList, ListNotificationsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/notifications/list` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /notifications/read
     * Marks notifications as read
     * Requires permission: notification.read
     * @param body Body parameters
     * @returns The request parameters
     */
    public static MarkAsRead(body: NotificationMarkReadBody): RequestParams<void, MarkAsReadErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/notifications/read`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /notifications/admin/settings
     * Gets notification admin settings
     * Requires permission: notification.admin
     * @returns The request parameters
     */
    public static GetAdminSettings(): RequestParams<NotificationAdminSettings, GetAdminSettingsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/notifications/admin/settings`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /notifications/admin/settings
     * Sets notification admin settings
     * Requires permission: notification.admin
     * @param body Body parameters
     * @returns The request parameters
     */
    public static SetAdminSettings(body: NotificationAdminSettings): RequestParams<void, SetAdminSettingsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/notifications/admin/settings`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_EMAILS", handler.badRequestInvalidEmails)
                    .add(400, "INVALID_MAX_AGE", handler.badRequestInvalidMaxAge)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for CountUnread
 */
export type CountUnreadErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for ListNotifications
 */
export type ListNotificationsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for ListNotifications
 */
export interface ListNotificationsQueryParameters {
    /**
     * Filter notification newer than this timestamp (Unix milliseconds)
     */
    filterDateFrom?: number;

    /**
     * Filter notification older than this timestamp (Unix milliseconds)
     */
    filterDateTo?: number;

    /**
     * Max number of notification. Hard limit: 256
     */
    limit?: number;

    /**
     * Continuation token, to request more pages
     */
    continuation?: number;
}

/**
 * Error handler for MarkAsRead
 */
export type MarkAsReadErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetAdminSettings
 */
export type GetAdminSettingsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for SetAdminSettings
 */
export type SetAdminSettingsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid max age
     */
    badRequestInvalidMaxAge: () => void;

    /**
     * Invalid emails list
     */
    badRequestInvalidEmails: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

